import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { Tooltip } from '../overlays/Tooltip';

export interface InfoTipProps {
	/** What the control or value means. Keep it to a sentence or two. */
	content: ReactNode;
	/**
	 * The button's accessible name. Defaults to "More information"; name the
	 * thing explained when several sit close together ("About My accounts").
	 */
	label?: string;
	className?: string;
}

/**
 * PD-16955 — the info icon that explains a control on demand, in place of a
 * hint line under it (owner, 2026-10-10: no on-screen help text on working
 * pages). Hover shows it on a desktop; a tap shows it on a phone, where a
 * hover tooltip alone would never appear. It is a real button so it can be
 * reached by keyboard and read by a screen reader.
 *
 * Never nest it inside another button (a chip, a row action): put it beside
 * the control it explains.
 */
export function InfoTip({
	content,
	label,
	className,
}: InfoTipProps): ReactElement {
	const { t } = useTranslation();
	return (
		<Tooltip content={content} maxWidth="18rem">
			<button
				type="button"
				aria-label={label ?? t('More information')}
				className={cn(
					'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-secondary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
					className,
				)}
			>
				<FontAwesomeIcon icon={faCircleInfo} className="text-xs" />
			</button>
		</Tooltip>
	);
}
